import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Plus, Trash2, Edit2, X, Check, Calendar, MapPin, Users, Infinity as InfinityIcon, Sparkles, UploadCloud, Image as ImageIcon, Phone, Eye } from 'lucide-react';
import { API_URL as API } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import TournamentLeaderboard from '../TournamentLeaderboard';
import { getTournamentImage } from '../../pages/Tournaments';
function parsePrizeBreakdown(t) {
  if (!t) return { prize1st: null, prize2nd: null, prize3rd: null };

  let prize1st = t.prize_1st ? String(t.prize_1st).trim() : null;
  let prize2nd = t.prize_2nd ? String(t.prize_2nd).trim() : null;
  let prize3rd = t.prize_3rd ? String(t.prize_3rd).trim() : null;
  const raw = t.prize_pool || '';

  if (raw && (!prize1st || !prize2nd || !prize3rd)) {
    const parts = raw.split(/\||\n/);
    parts.forEach(part => {
      const cleanPart = part.replace(/^[^a-zA-Z0-9₹]+/, '').trim();
      if (/1st:/i.test(cleanPart)) {
        const val = cleanPart.replace(/.*?1st:\s*/i, '').trim();
        if (val && !prize1st) prize1st = val;
      } else if (/2nd:/i.test(cleanPart)) {
        const val = cleanPart.replace(/.*?2nd:\s*/i, '').trim();
        if (val && !prize2nd) prize2nd = val;
      } else if (/3rd:/i.test(cleanPart)) {
        const val = cleanPart.replace(/.*?3rd:\s*/i, '').trim();
        if (val && !prize3rd) prize3rd = val;
      }
    });
  }

  if (!prize1st && !prize2nd && !prize3rd && raw && !raw.includes('1st:')) {
    prize1st = raw.trim();
  }

  const formatVal = (val) => {
    if (!val || val.trim() === '' || val === 'null' || val === 'undefined') return null;
    const trimmed = val.trim();
    const cleanNum = trimmed.replace(/[^0-9]/g, '');
    if (cleanNum && !isNaN(Number(cleanNum)) && !/[a-zA-Z]/.test(trimmed)) {
      return `₹${Number(cleanNum).toLocaleString('en-IN')}`;
    }
    return trimmed;
  };

  return {
    prize1st: formatVal(prize1st),
    prize2nd: formatVal(prize2nd),
    prize3rd: formatVal(prize3rd),
  };
}

const getTodayStr = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentTimeStr = () => {
  const d = new Date();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export default function PartnerTournaments({ data, token, onChange, onToast }) {
  const { user } = useAuth();
  const profileName = user?.name || data?.name || '';
  const profilePhone = user?.phone || data?.phone || '';
  const profileEmail = user?.email || data?.email || '';

  const turfs = data?.turfs || [];
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Structured Prize Pool Breakdown State
  const [prize1st, setPrize1st] = useState('');
  const [prize2nd, setPrize2nd] = useState('');
  const [prize3rd, setPrize3rd] = useState('');
  const [customPrizes, setCustomPrizes] = useState([]); // [{ id, title, prize }]

  // Image Upload State
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Tournament Detail & Registrations Modal State for Partner
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Extend Registration Date inline state
  const [extendingRegDate, setExtendingRegDate] = useState(false);
  const [newRegCloseDate, setNewRegCloseDate] = useState('');
  const [savingExtend, setSavingExtend] = useState(false);

  const handleExtendRegDate = async () => {
    if (!newRegCloseDate || !selectedTournament) return;
    const tourneyId = selectedTournament._id || selectedTournament.id;
    if (!tourneyId) return;
    setSavingExtend(true);
    try {
      const newDateISO = new Date(newRegCloseDate).toISOString();
      const res = await axios.put(`${API}/tournaments/${tourneyId}`, {
        registration_close_date: newDateISO
      }, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data;
      const updatedDate = updated?.registration_close_date || newDateISO;
      setSelectedTournament(prev => (prev ? { ...prev, registration_close_date: updatedDate } : null));
      setTournaments(prev => prev.map(t => ((t._id && t._id === tourneyId) || (t.id && t.id === tourneyId)) ? { ...t, registration_close_date: updatedDate } : t));
      setExtendingRegDate(false);
      setNewRegCloseDate('');
      if (onToast) onToast('📅 Registration date extended successfully!', 'success');
    } catch (err) {
      if (onToast) onToast(err?.response?.data?.detail || err?.message || 'Failed to extend registration date.', 'error');
    } finally {
      setSavingExtend(false);
    }
  };

  // ── Leaderboard Editor State ──────────────────────────────────────────────
  const DEFAULT_ENTRIES = [
    { id: 1, rank: 1, rank_label: '1st Place', team_name: '', captain_name: '', prize: '', notes: '' },
    { id: 2, rank: 2, rank_label: '2nd Place', team_name: '', captain_name: '', prize: '', notes: '' },
    { id: 3, rank: 3, rank_label: '3rd Place', team_name: '', captain_name: '', prize: '', notes: '' },
  ];
  const [showLeaderboardEditor, setShowLeaderboardEditor] = useState(false);
  const [leaderboardEntries, setLeaderboardEntries] = useState(DEFAULT_ENTRIES);
  const [savingLeaderboard, setSavingLeaderboard] = useState(false);
  const [leaderboardKey, setLeaderboardKey] = useState(0); // force re-fetch viewer

  const fetchLeaderboard = async (tId) => {
    try {
      const res = await axios.get(`${API}/tournaments/${tId}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      if (data.length > 0) {
        // Merge fetched into default template (ensuring top 3 slots always present)
        const map = {};
        data.forEach(e => { map[e.rank] = e; });
        const base = [1, 2, 3].map(r => map[r] ? {
          id: map[r]._id, rank: r,
          rank_label: map[r].rank_label || (r === 1 ? '1st Place' : r === 2 ? '2nd Place' : '3rd Place'),
          team_name: map[r].team_name || '', captain_name: map[r].captain_name || '',
          prize: map[r].prize || '', notes: map[r].notes || ''
        } : { id: r, rank: r, rank_label: r === 1 ? '1st Place' : r === 2 ? '2nd Place' : '3rd Place', team_name: '', captain_name: '', prize: '', notes: '' });
        const extras = data.filter(e => e.rank > 3).map(e => ({
          id: e._id, rank: e.rank, rank_label: e.rank_label,
          team_name: e.team_name || '', captain_name: e.captain_name || '',
          prize: e.prize || '', notes: e.notes || ''
        }));
        setLeaderboardEntries([...base, ...extras]);
      } else {
        setLeaderboardEntries(DEFAULT_ENTRIES);
      }
    } catch {
      setLeaderboardEntries(DEFAULT_ENTRIES);
    }
  };

  const handleSaveLeaderboard = async () => {
    const valid = leaderboardEntries.filter(e => e.rank_label.trim());
    setSavingLeaderboard(true);
    try {
      await axios.put(`${API}/tournaments/${selectedTournament._id}/leaderboard`, {
        entries: valid.map(e => ({
          rank: e.rank,
          rank_label: e.rank_label,
          team_name: e.team_name || null,
          captain_name: e.captain_name || null,
          prize: e.prize || null,
          notes: e.notes || null,
        }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      setLeaderboardKey(k => k + 1);
      setShowLeaderboardEditor(false);
      if (onToast) onToast('🏆 Leaderboard saved successfully!', 'success');
    } catch (err) {
      if (onToast) onToast(err?.response?.data?.detail || 'Failed to save leaderboard.', 'error');
    } finally {
      setSavingLeaderboard(false);
    }
  };

  const addLeaderboardRow = () => {
    const nextRank = leaderboardEntries.length > 0 ? Math.max(...leaderboardEntries.map(e => e.rank)) + 1 : 4;
    setLeaderboardEntries(prev => [...prev, { id: Date.now(), rank: nextRank, rank_label: '', team_name: '', captain_name: '', prize: '', notes: '' }]);
  };

  const removeLeaderboardRow = (id) => {
    setLeaderboardEntries(prev => prev.filter(e => e.id !== id));
  };

  const updateLeaderboardRow = (id, field, value) => {
    setLeaderboardEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const openTournamentModal = async (t) => {
    setSelectedTournament(t);
    setSelectedTeam(null);
    setShowLeaderboardEditor(false);
    setLeaderboardKey(0);
    setLoadingRegistrations(true);
    // Fetch registrations and leaderboard in parallel
    Promise.all([
      axios.get(`${API}/tournaments/${t._id}/registrations`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setRegistrations(res.data || []))
        .catch(() => setRegistrations([])),
      fetchLeaderboard(t._id),
    ]).finally(() => setLoadingRegistrations(false));
  };

  const handleStatusChange = async (tId, newStatus, targetTournament) => {
    const tourney = targetTournament || selectedTournament || tournaments.find(t => (t._id === tId || t.id === tId));

    if (newStatus === 'cancelled') {
      const createdAtStr = tourney?.created_at;
      if (createdAtStr) {
        const createdAt = new Date(createdAtStr);
        const hoursElapsed = (new Date().getTime() - createdAt.getTime()) / (1000 * 60 * 60);
        if (hoursElapsed > 24) {
          const confirmCancel = window.confirm(
            "⚠️ Warning: This tournament was created more than 24 hours ago. Cancelling it after 24 hours will affect registered teams and players.\n\nAre you sure you want to cancel this tournament?"
          );
          if (!confirmCancel) return;
        }
      }
    }

    setUpdatingStatus(true);
    try {
      const res = await axios.put(`${API}/tournaments/${tId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOwnerTournaments();
      if (selectedTournament && (selectedTournament._id === tId || selectedTournament.id === tId)) {
        setSelectedTournament(res.data);
      }
      if (onToast) onToast(`Tournament status updated to ${newStatus.toUpperCase()}`, 'success');
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatPhoneInput = (val) => {
    if (!val) return '';
    let str = val.toString().trim();
    if (str.startsWith('+91')) {
      str = str.slice(3).trim();
    } else if (str.startsWith('+')) {
      str = str.slice(1).trim();
    }
    let digits = str.replace(/\D/g, '');
    if (digits.length > 10 && digits.startsWith('91')) {
      digits = digits.slice(2);
    }
    // Indian mobile number starting digit check (must start with 6, 7, 8, or 9)
    digits = digits.replace(/^[^6-9]+/, '');
    digits = digits.slice(0, 10);
    if (!digits) return '';
    return `+91 ${digits}`;
  };

  const [form, setForm] = useState({
    name: '',
    tournament_type: 'Knockout',
    tournament_type_custom: '',
    selected_turf_id: '', // 'custom' or turf._id
    turf_name: '',
    address: '',
    city: '',
    start_date_val: '',
    start_time_val: '09:00',
    end_date_val: '',
    end_time_val: '18:00',
    reg_close_date: '',
    reg_close_time: '23:59',
    max_teams: 16,
    is_unlimited_teams: false,
    team_size: 5,
    sport: 'Football',
    sport_custom: '',
    entry_fee: '',
    prize_pool: '',
    image_url: '',
    description: '',
    status: 'upcoming',
    organizer_name: profileName,
    organizer_phone: formatPhoneInput(profilePhone),
    organizer_whatsapp: formatPhoneInput(profilePhone),
    organizer_email: profileEmail,
    min_age: '',
    max_age: '',
    is_open_age: true,
    min_team_size: 5,
    max_team_size: 5
  });

  // Sync profile details if data loads after mount
  useEffect(() => {
    if (!editingTournament) {
      setForm(prev => ({
        ...prev,
        organizer_name: prev.organizer_name || profileName,
        organizer_phone: prev.organizer_phone || formatPhoneInput(profilePhone),
        organizer_whatsapp: prev.organizer_whatsapp || formatPhoneInput(profilePhone),
        organizer_email: prev.organizer_email || profileEmail
      }));
    }
  }, [profileName, profilePhone, profileEmail]);

  const fetchOwnerTournaments = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tournaments/owner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTournaments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwnerTournaments();
  }, [token]);

  const addCustomPrize = () => {
    setCustomPrizes(prev => [...prev, { id: Date.now(), title: '', prize: '' }]);
  };

  const updateCustomPrize = (index, field, value) => {
    setCustomPrizes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeCustomPrize = (index) => {
    setCustomPrizes(prev => prev.filter((_, i) => i !== index));
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setUploadingBanner(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/tournament-banner`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });
      if (res.data && res.data.url) {
        setForm(prev => ({ ...prev, image_url: res.data.url }));
        if (onToast) onToast('Banner image uploaded successfully!', 'success');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.msg || 'Failed to upload image banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const computeAutoStatus = (sDate, eDate, currentStatus = 'upcoming') => {
    if (currentStatus === 'cancelled') return 'cancelled';
    if (!sDate || !eDate) return currentStatus || 'upcoming';
    const now = new Date().getTime();
    const start = new Date(sDate).getTime();
    const end = new Date(eDate).getTime();
    if (now < start) return 'upcoming';
    if (now >= start && now <= end) return 'live';
    return 'completed';
  };

  const handleDateChange = (field, value) => {
    setForm(prev => {
      let val = value;
      const today = getTodayStr();
      if ((field === 'start_date_val' || field === 'reg_close_date') && val < today) {
        val = today;
      }
      const newForm = { ...prev, [field]: val };
      if (field === 'start_date_val' && newForm.end_date_val && newForm.end_date_val < val) {
        newForm.end_date_val = val;
      }
      if (field === 'start_date_val' && newForm.reg_close_date && newForm.reg_close_date > val) {
        newForm.reg_close_date = val;
      }
      const fullStart = newForm.start_date_val && newForm.start_time_val ? `${newForm.start_date_val}T${newForm.start_time_val}` : '';
      const fullEnd = newForm.end_date_val && newForm.end_time_val ? `${newForm.end_date_val}T${newForm.end_time_val}` : '';
      const autoStatus = computeAutoStatus(fullStart, fullEnd, newForm.status);
      return { ...newForm, status: autoStatus };
    });
  };

  const openCreate = () => {
    setEditingTournament(null);
    const defaultTurf = turfs.length > 0 ? turfs[0] : null;
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(now);
    nextWeek.setDate(nextWeek.getDate() + 7);

    setPrize1st('');
    setPrize2nd('');
    setPrize3rd('');
    setCustomPrizes([]);

    const startDateStr = tomorrow.toISOString().slice(0, 16);
    const endDateStr = nextWeek.toISOString().slice(0, 16);
    const initialStatus = computeAutoStatus(startDateStr, endDateStr, 'upcoming');

    setForm({
      name: '',
      tournament_type: 'Knockout',
      tournament_type_custom: '',
      selected_turf_id: defaultTurf ? (defaultTurf._id || defaultTurf.id) : 'custom',
      turf_name: defaultTurf ? defaultTurf.name : '',
      address: defaultTurf ? (defaultTurf.location || defaultTurf.address || '') : '',
      city: defaultTurf ? (defaultTurf.city || '') : '',
      start_date_val: startDateStr.slice(0, 10),
      start_time_val: startDateStr.slice(11, 16) || '09:00',
      end_date_val: endDateStr.slice(0, 10),
      end_time_val: endDateStr.slice(11, 16) || '18:00',
      reg_close_date: startDateStr.slice(0, 10),
      reg_close_time: startDateStr.slice(11, 16) || '23:59',
      max_teams: 16,
      is_unlimited_teams: false,
      team_size: 5,
      sport: 'Football',
      sport_custom: '',
      entry_fee: '',
      prize_pool: '',
      image_url: '',
      description: '',
      status: initialStatus,
      organizer_name: profileName,
      organizer_phone: formatPhoneInput(profilePhone),
      organizer_whatsapp: formatPhoneInput(profilePhone),
      organizer_email: profileEmail,
      min_age: '',
      max_age: '',
      is_open_age: true,
      min_team_size: 5,
      max_team_size: 5
    });
    setShowForm(true);
    setError('');
  };

  const openEdit = (t) => {
    setEditingTournament(t);

    // Parse prize pool if formatted
    const rawPrize = t.prize_pool || '';
    setPrize1st('');
    setPrize2nd('');
    setPrize3rd('');
    setCustomPrizes([]);

    if (rawPrize) {
      const parts = rawPrize.split(' | ');
      const customs = [];
      parts.forEach(part => {
        if (part.includes('1st:')) {
          setPrize1st(part.replace(/.*?1st:\s*/, ''));
        } else if (part.includes('2nd:')) {
          setPrize2nd(part.replace(/.*?2nd:\s*/, ''));
        } else if (part.includes('3rd:')) {
          setPrize3rd(part.replace(/.*?3rd:\s*/, ''));
        } else if (part.includes(':')) {
          const [tTitle, tPrize] = part.split(':');
          customs.push({
            id: Date.now() + Math.random(),
            title: tTitle.replace(/^[^\w]+/, '').trim(),
            prize: (tPrize || '').trim()
          });
        } else {
          if (!prize1st) setPrize1st(rawPrize);
        }
      });
      if (customs.length > 0) setCustomPrizes(customs);
    }

    const sDateStr = t.start_date ? t.start_date.slice(0, 16) : '';
    const eDateStr = t.end_date ? t.end_date.slice(0, 16) : '';
    const autoStatus = computeAutoStatus(sDateStr, eDateStr, t.status || 'upcoming');
    const regClose = t.registration_close_date ? t.registration_close_date.slice(0, 16) : '';

    const turfMatch = turfs.find(tf => String(tf._id || tf.id) === String(t.turf_id));
    const resolvedCity = t.city || (turfMatch ? (turfMatch.city || '') : '') || '';

    setForm({
      name: t.name || '',
      tournament_type: t.tournament_type || 'Knockout',
      tournament_type_custom: t.tournament_type_custom || '',
      selected_turf_id: t.turf_id || 'custom',
      turf_name: t.turf_name || '',
      address: t.address || '',
      city: resolvedCity,
      start_date_val: sDateStr ? sDateStr.slice(0, 10) : '',
      start_time_val: sDateStr ? sDateStr.slice(11, 16) : '09:00',
      end_date_val: eDateStr ? eDateStr.slice(0, 10) : '',
      end_time_val: eDateStr ? eDateStr.slice(11, 16) : '18:00',
      reg_close_date: regClose ? regClose.slice(0, 10) : '',
      reg_close_time: regClose ? regClose.slice(11, 16) : '23:59',
      max_teams: t.max_teams || 16,
      is_unlimited_teams: t.is_unlimited_teams || false,
      team_size: t.team_size || 5,
      sport: t.sport || 'Football',
      sport_custom: t.sport_custom || '',
      entry_fee: t.entry_fee || '',
      prize_pool: rawPrize,
      image_url: t.image_url || '',
      description: t.description || '',
      status: autoStatus,
      organizer_name: t.organizer_name || t.owner_name || profileName,
      organizer_phone: formatPhoneInput(t.organizer_phone || t.owner_phone || profilePhone),
      organizer_whatsapp: t.organizer_whatsapp ? formatPhoneInput(t.organizer_whatsapp) : '',
      organizer_email: t.organizer_email || profileEmail,
      min_age: t.min_age || '',
      max_age: t.max_age || '',
      is_open_age: t.is_open_age !== undefined ? t.is_open_age : true,
      min_team_size: t.min_team_size || t.team_size || 5,
      max_team_size: t.max_team_size || t.team_size || 5
    });
    setShowForm(true);
    setError('');
  };

  const handleTurfSelect = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setForm(prev => ({
        ...prev,
        selected_turf_id: 'custom',
        turf_name: '',
        address: '',
        city: ''
      }));
    } else {
      const selected = turfs.find(t => (t._id || t.id) === val);
      setForm(prev => ({
        ...prev,
        selected_turf_id: val,
        turf_name: selected ? selected.name : '',
        address: selected ? (selected.location || selected.address || '') : '',
        city: selected ? (selected.city || '') : ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Tournament name is required');
    if (!form.turf_name.trim()) return setError('Turf name is required');
    if (!form.address.trim()) return setError('Address is required');
    if (!form.city.trim()) return setError('City is required');
    if (!form.start_date_val || !form.start_time_val) return setError('Start date and start time are required');
    if (!form.end_date_val || !form.end_time_val) return setError('End date and end time are required');
    if (!form.reg_close_date || !form.reg_close_time) return setError('Registration closing date and time are required');

    const todayStr = getTodayStr();
    const currentTimeStr = getCurrentTimeStr();

    if (form.start_date_val < todayStr) {
      return setError('Start date cannot be in the past');
    }
    if (form.start_date_val === todayStr && form.start_time_val < currentTimeStr) {
      return setError('Start time cannot be in the past');
    }

    if (form.end_date_val < form.start_date_val) {
      return setError('End date cannot be earlier than start date');
    }
    if (form.end_date_val === form.start_date_val && form.end_time_val <= form.start_time_val) {
      return setError('End time must be after start time on the same day');
    }

    if (form.reg_close_date < todayStr) {
      return setError('Registration closing date cannot be in the past');
    }
    if (form.reg_close_date > form.start_date_val) {
      return setError('Registration closing date cannot be after the tournament start date');
    }
    if (form.reg_close_date === form.start_date_val && form.reg_close_time > form.start_time_val) {
      return setError('Registration closing time cannot be after the tournament start time');
    }

    // Organizer Name Validation (No numbers or special characters)
    const orgNameTrim = (form.organizer_name || '').trim();
    if (!orgNameTrim) return setError('Organizer Name is required');
    if (/[^a-zA-Z\s.-]/.test(orgNameTrim)) {
      return setError('Organizer Name must only contain letters and spaces (no numbers or special characters)');
    }

    // Contact Phone Number Validation (+91 requirement & 10 digits starting with 6, 7, 8, 9)
    const phoneTrim = (form.organizer_phone || '').trim();
    const phoneClean = phoneTrim.replace(/\s+/g, '');
    const isValidIndianMobile = (p) => /^(\+91)?[6-9]\d{9}$/.test(p);
    if (!phoneTrim || !isValidIndianMobile(phoneClean)) {
      return setError('Contact Phone Number is required and must start with +91 followed by a 10-digit Indian mobile number starting with 6, 7, 8, or 9 (e.g. +91 9876543210)');
    }

    // WhatsApp Number Validation (Optional)
    const waTrim = (form.organizer_whatsapp || '').trim();
    if (waTrim) {
      const waClean = waTrim.replace(/\s+/g, '');
      if (!isValidIndianMobile(waClean)) {
        return setError('WhatsApp Number must start with +91 followed by a 10-digit Indian mobile number starting with 6, 7, 8, or 9 (e.g. +91 9876543210)');
      }
    }

    // Organizer Email Validation (Optional)
    const emailTrim = (form.organizer_email || '').trim();
    if (emailTrim) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTrim)) {
        return setError('Please enter a valid Organizer Email address (e.g. name@example.com)');
      }
    }

    setSaving(true);
    setError('');

    // Compile structured prize pool
    const prizeParts = [];
    if (prize1st.trim()) prizeParts.push(`🥇 1st: ${prize1st.trim()}`);
    if (prize2nd.trim()) prizeParts.push(`🥈 2nd: ${prize2nd.trim()}`);
    if (prize3rd.trim()) prizeParts.push(`🥉 3rd: ${prize3rd.trim()}`);
    customPrizes.forEach(cp => {
      if (cp.title.trim() && cp.prize.trim()) {
        prizeParts.push(`🎖️ ${cp.title.trim()}: ${cp.prize.trim()}`);
      }
    });
    const compiledPrizePool = prizeParts.length > 0 ? prizeParts.join(' | ') : form.prize_pool.trim();

    const regCloseIso = form.reg_close_date && form.reg_close_time
      ? new Date(`${form.reg_close_date}T${form.reg_close_time}`).toISOString()
      : null;

    const payload = {
      name: form.name.trim(),
      tournament_type: form.tournament_type,
      tournament_type_custom: form.tournament_type === 'Custom' ? form.tournament_type_custom.trim() : null,
      turf_id: form.selected_turf_id !== 'custom' ? form.selected_turf_id : null,
      turf_name: form.turf_name.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      start_date: new Date(`${form.start_date_val}T${form.start_time_val}`).toISOString(),
      end_date: new Date(`${form.end_date_val}T${form.end_time_val}`).toISOString(),
      registration_close_date: regCloseIso,
      max_teams: form.is_unlimited_teams ? null : parseInt(form.max_teams || 0),
      is_unlimited_teams: form.is_unlimited_teams,
      team_size: parseInt(form.team_size || 5),
      min_team_size: parseInt(form.min_team_size || form.team_size || 5),
      max_team_size: parseInt(form.max_team_size || form.team_size || 5),
      sport: form.sport,
      sport_custom: form.sport === 'Custom' ? form.sport_custom.trim() : null,
      entry_fee: parseFloat(form.entry_fee || 0),
      prize_pool: compiledPrizePool,
      image_url: form.image_url.trim(),
      description: form.description.trim(),
      status: form.status,
      min_age: form.is_open_age ? null : (form.min_age ? parseInt(form.min_age) : null),
      max_age: form.is_open_age ? null : (form.max_age ? parseInt(form.max_age) : null),
      is_open_age: form.is_open_age,
      organizer_name: form.organizer_name.trim(),
      organizer_phone: phoneTrim,
      organizer_whatsapp: waTrim || null,
      organizer_email: emailTrim || null
    };

    try {
      if (editingTournament) {
        await axios.put(`${API}/tournaments/${editingTournament._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (onToast) onToast('Tournament updated successfully!', 'success');
      } else {
        await axios.post(`${API}/tournaments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (onToast) onToast('Tournament created successfully!', 'success');
      }
      setShowForm(false);
      setEditingTournament(null);
      fetchOwnerTournaments();
      if (onChange) onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save tournament');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tournament?')) return;
    try {
      await axios.delete(`${API}/tournaments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast('Tournament deleted', 'success');
      fetchOwnerTournaments();
      if (onChange) onChange();
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to delete tournament');
    }
  };

  const handleToggleActive = async (t) => {
    const newActive = !t.is_active;
    try {
      await axios.put(`${API}/tournaments/${t._id}`, { is_active: newActive }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onToast) onToast(newActive ? 'Tournament Activated!' : 'Tournament Deactivated!', 'success');
      fetchOwnerTournaments();
      if (onChange) onChange();
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to toggle active status');
    }
  };

  return (
    <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#0D1F0F', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Trophy color="#084734" size={28} /> Tournaments
          </h2>
          <p style={{ color: '#374151', fontSize: '0.9rem', margin: '4px 0 0', fontWeight: '500' }}>
            Create and manage sports tournaments for players & teams
          </p>
        </div>
        <button onClick={openCreate} style={btnPrimary}>
          <Plus size={18} /> Create Tournament
        </button>
      </div>

      {/* CREATE / EDIT FORM */}
      {showForm && (
        <form onSubmit={handleSubmit} style={formCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gridColumn: '1 / -1', paddingBottom: '0.5rem', borderBottom: '1px solid #E9EDE8' }}>
            <h3 style={{ fontWeight: '800', fontSize: '1.2rem', color: '#0D1F0F', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={20} color="#084734" />
              {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
            </h3>
            <button type="button" onClick={() => { setShowForm(false); setEditingTournament(null); }} style={closeBtnStyle}>
              <X size={20} />
            </button>
          </div>

          {/* Tournament Name */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Tournament Name *</label>
            <input
              required
              placeholder="e.g. TurfX Summer Champions Cup 2026"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              style={inputStyle}
            />
          </div>

          {/* Tournament Type */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Tournament Type *</label>
            <select
              value={form.tournament_type}
              onChange={e => setForm({ ...form, tournament_type: e.target.value })}
              style={inputStyle}
            >
              <option value="Knockout">Knockout</option>
              <option value="League">League</option>
              <option value="Round Robin">Round Robin</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* Custom Tournament Type Input */}
          {form.tournament_type === 'Custom' && (
            <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Custom Tournament Format *</label>
              <input
                required
                placeholder="Enter custom format (e.g. Swiss System / Double Elimination)"
                value={form.tournament_type_custom}
                onChange={e => setForm({ ...form, tournament_type_custom: e.target.value })}
                style={inputStyle}
              />
            </div>
          )}

          {/* Turf Selection */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Select Venue / Turf Location *</label>
            <select
              value={form.selected_turf_id}
              onChange={handleTurfSelect}
              style={inputStyle}
            >
              {turfs.map(t => (
                <option key={t._id || t.id} value={t._id || t.id}>
                  {t.name} ({t.city || t.location})
                </option>
              ))}
              <option value="custom">➕ Other / Add Custom Turf Name & Address</option>
            </select>
          </div>

          {/* Turf Name & Address & City Inputs */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Turf Name *</label>
            <input
              required
              placeholder="Turf Name"
              value={form.turf_name}
              onChange={e => setForm({ ...form, turf_name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>Turf Address *</label>
            <input
              required
              placeholder="Full address details"
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={fieldGroup}>
            <label style={labelStyle}>City *</label>
            <input
              type="text"
              required
              placeholder="e.g. Bengaluru, Mumbai, Delhi"
              value={form.city}
              onChange={e => setForm({ ...form, city: e.target.value })}
              style={inputStyle}
            />
          </div>

          {/* Sports Type */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Sports Type *</label>
            <select
              value={form.sport}
              onChange={e => setForm({ ...form, sport: e.target.value })}
              style={inputStyle}
            >
              <option value="Football">Football</option>
              <option value="Cricket">Cricket</option>
              <option value="Badminton">Badminton</option>
              <option value="Tennis">Tennis</option>
              <option value="Basketball">Basketball</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          {/* Custom Sport Input */}
          {form.sport === 'Custom' && (
            <div style={fieldGroup}>
              <label style={labelStyle}>Custom Sport Name *</label>
              <input
                required
                placeholder="e.g. Volleyball / Table Tennis"
                value={form.sport_custom}
                onChange={e => setForm({ ...form, sport_custom: e.target.value })}
                style={inputStyle}
              />
            </div>
          )}

          {/* Team Size & Players Allowed Range */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1', background: '#F8FAF7', padding: '1.25rem', borderRadius: '16px', border: '1px solid #EEF2E6' }}>
            <label style={{ ...labelStyle, marginBottom: '4px' }}>👥 Number of Players Allowed Range per Team *</label>
            <div style={{ fontSize: '0.78rem', color: '#6B7280', marginBottom: '12px' }}>
              Set playing format (e.g. 5v5) and minimum to maximum range of players allowed per registered team squad.
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>On-Field Format (e.g. 5 for 5v5) *</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  required
                  placeholder="e.g. 5"
                  value={form.team_size}
                  onWheel={e => e.target.blur()}
                  onChange={e => setForm({ ...form, team_size: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Min Players Allowed *</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  required
                  placeholder="e.g. 5"
                  value={form.min_team_size}
                  onWheel={e => e.target.blur()}
                  onChange={e => setForm({ ...form, min_team_size: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Max Players Allowed *</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  required
                  placeholder="e.g. 11"
                  value={form.max_team_size}
                  onWheel={e => e.target.blur()}
                  onChange={e => setForm({ ...form, max_team_size: e.target.value })}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Number of Teams Allowed to Register + Infinity Button */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Max Teams Allowed to Register *</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="number"
                min="2"
                disabled={form.is_unlimited_teams}
                placeholder={form.is_unlimited_teams ? "Unlimited Teams Allowed (∞)" : "Enter team limit, e.g. 16"}
                value={form.is_unlimited_teams ? '' : form.max_teams}
                onWheel={e => e.target.blur()}
                onChange={e => setForm({ ...form, max_teams: e.target.value })}
                style={{ ...inputStyle, flex: 1, opacity: form.is_unlimited_teams ? 0.6 : 1 }}
              />
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, is_unlimited_teams: !prev.is_unlimited_teams }))}
                style={{
                  padding: '12px 20px',
                  borderRadius: '12px',
                  border: `2px solid ${form.is_unlimited_teams ? '#084734' : '#E9EDE8'}`,
                  background: form.is_unlimited_teams ? '#084734' : '#F9FAFB',
                  color: form.is_unlimited_teams ? '#CEF17B' : '#374151',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                <InfinityIcon size={20} />
                {form.is_unlimited_teams ? 'Unlimited' : 'Infinity'}
              </button>
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6B7280' }}>
              Click Infinity (∞) if there is no maximum team limit.
            </p>
          </div>

          {/* Start Date & Time (Split) */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Starts From (Start Details) *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Start Date *</label>
                <input
                  type="date"
                  required
                  min={getTodayStr()}
                  value={form.start_date_val}
                  onChange={e => handleDateChange('start_date_val', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Start Time *</label>
                <input
                  type="time"
                  required
                  min={form.start_date_val === getTodayStr() ? getCurrentTimeStr() : undefined}
                  value={form.start_time_val}
                  onChange={e => handleDateChange('start_time_val', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* End Date & Time (Split) */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Ends On (End Details) *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>End Date *</label>
                <input
                  type="date"
                  required
                  min={form.start_date_val || getTodayStr()}
                  value={form.end_date_val}
                  onChange={e => handleDateChange('end_date_val', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>End Time *</label>
                <input
                  type="time"
                  required
                  min={form.end_date_val === form.start_date_val ? form.start_time_val : undefined}
                  value={form.end_time_val}
                  onChange={e => handleDateChange('end_time_val', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Registration Closing Date & Time (Split) */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Registration Closing Details *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Registration Closing Date *</label>
                <input
                  type="date"
                  required
                  min={getTodayStr()}
                  max={form.start_date_val || undefined}
                  value={form.reg_close_date}
                  onChange={e => handleDateChange('reg_close_date', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Registration Closing Time *</label>
                <input
                  type="time"
                  required
                  min={form.reg_close_date === getTodayStr() ? getCurrentTimeStr() : undefined}
                  value={form.reg_close_time}
                  onChange={e => handleDateChange('reg_close_time', e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '4px 0 0' }}>
              Registrations will automatically close after this date & time.
            </p>
          </div>

          {/* Age Limit Category */}
          <div style={{ gridColumn: '1 / -1', background: '#F8FAF7', padding: '1.25rem', borderRadius: '16px', border: '1px solid #EEF2E6' }}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ ...labelStyle, margin: 0, fontSize: '1rem', fontWeight: '800', color: '#0D1F0F' }}>🎂 Age Limit Category</label>
              <div style={{ fontSize: '0.82rem', color: '#4B5563', marginTop: '2px' }}>Specify allowed age range or leave open for all ages</div>
            </div>

            {form.is_open_age ? (
              <div style={{ background: '#FFFFFF', padding: '1.25rem', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '0.88rem', background: '#DCEFB8', color: '#084734', padding: '6px 14px', borderRadius: '20px', fontWeight: '800' }}>
                    ✓ Open for All Ages Active
                  </span>
                  <span style={{ fontSize: '0.82rem', color: '#6B7280' }}>
                    No age restriction set. All age groups are allowed to participate.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, is_open_age: false }))}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: '1.5px solid #084734',
                    background: '#084734',
                    color: '#CEF17B',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  ➕ Specify Custom Age Group
                </button>
              </div>
            ) : (
              <div style={{ background: '#FFFFFF', padding: '1.25rem', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h5 style={{ margin: 0, fontSize: '0.88rem', fontWeight: '700', color: '#111827' }}>Specify Custom Age Group</h5>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '16px' }}>
                  <div>
                    <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Min Age (From)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="e.g. 18"
                      value={form.min_age}
                      onWheel={e => e.target.blur()}
                      onChange={e => setForm(prev => ({ ...prev, min_age: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: '0.78rem' }}>Max Age (To)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="e.g. 35"
                      value={form.max_age}
                      onWheel={e => e.target.blur()}
                      onChange={e => setForm(prev => ({ ...prev, max_age: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, is_open_age: true, min_age: '', max_age: '' }))}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '10px',
                      border: '1.5px solid #D1D5DB',
                      background: '#F9FAFB',
                      color: '#374151',
                      fontWeight: '700',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    Allow all ages
                  </button>
                  <span style={{ fontSize: '0.78rem', color: '#6B7280' }}>
                    Click to remove age limits and allow all ages.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ORGANIZER / PARTNER CONTACT INFORMATION */}
          <div style={{ gridColumn: '1 / -1', background: '#F0FDF4', padding: '1.25rem', borderRadius: '16px', border: '1.5px solid #BBF7D0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Phone size={18} color="#084734" />
              <div>
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: '#084734' }}>
                  Organizer Contact Information
                </h4>
                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#374151' }}>
                  These contact details will be visible to players & Admin for inquiries and support
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Organizer Name *</label>
                <input
                  required
                  placeholder="Organizer / Partner Name"
                  value={form.organizer_name}
                  onChange={e => setForm({ ...form, organizer_name: e.target.value.replace(/[^a-zA-Z\s.-]/g, '') })}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Contact Phone Number *</label>
                <input
                  required
                  placeholder="e.g. +91 9876543210"
                  maxLength={14}
                  value={form.organizer_phone}
                  onChange={e => setForm(prev => ({ ...prev, organizer_phone: formatPhoneInput(e.target.value) }))}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>WhatsApp Number <span style={{ color: '#6B7280', textTransform: 'none' }}>(Optional)</span></label>
                <input
                  placeholder="e.g. +91 9876543210 (Leave blank if no WhatsApp)"
                  maxLength={14}
                  value={form.organizer_whatsapp}
                  onChange={e => setForm(prev => ({ ...prev, organizer_whatsapp: formatPhoneInput(e.target.value) }))}
                  style={inputStyle}
                />
                <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#6B7280' }}>
                  Auto-filled with your phone number. You can edit this number or clear it if you don't want a WhatsApp chat button.
                </p>
              </div>

              <div>
                <label style={labelStyle}>Organizer Email <span style={{ color: '#6B7280', textTransform: 'none' }}>(Optional)</span></label>
                <input
                  type="email"
                  placeholder="organizer@example.com"
                  value={form.organizer_email}
                  onChange={e => setForm({ ...form, organizer_email: e.target.value })}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Entry Fee & Prize Pool */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Entry Fee per Team (₹)</label>
            <input
              type="number"
              min="0"
              placeholder="e.g. 1500 (Leave 0 for free entry)"
              value={form.entry_fee}
              onWheel={e => e.target.blur()}
              onChange={e => setForm({ ...form, entry_fee: e.target.value })}
              style={inputStyle}
            />
          </div>

          {/* PRIZE POOL BREAKDOWN BUILDER */}
          <div style={{ gridColumn: '1 / -1', background: '#F8FAF7', padding: '1.25rem', borderRadius: '16px', border: '1.5px solid #EEF2E6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Trophy size={20} color="#D97706" />
              <div>
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: '#0D1F0F' }}>
                  Prize Pool & Rewards Breakdown
                </h4>
                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#6B7280' }}>
                  Specify rewards for 1st, 2nd, 3rd positions and custom category awards
                </p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              {/* 1st Place */}
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '800', color: '#161616', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                  🥇 1st Place (Winner) <span style={{ fontSize: '0.7rem', color: '#9CA3AF', fontWeight: '500' }}>(Optional)</span>
                </label>
                <input
                  placeholder="e.g. ₹50,000 + Trophy"
                  value={prize1st}
                  onChange={e => setPrize1st(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* 2nd Place */}
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '800', color: '#161616', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                  🥈 2nd Place (Runner-up) <span style={{ fontSize: '0.7rem', color: '#9CA3AF', fontWeight: '500' }}>(Optional)</span>
                </label>
                <input
                  placeholder="e.g. ₹25,000 + Trophy"
                  value={prize2nd}
                  onChange={e => setPrize2nd(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* 3rd Place */}
              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: '800', color: '#161616', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                  🥉 3rd Place (2nd Runner-up) <span style={{ fontSize: '0.7rem', color: '#9CA3AF', fontWeight: '500' }}>(Optional)</span>
                </label>
                <input
                  placeholder="e.g. ₹10,000 + Medals"
                  value={prize3rd}
                  onChange={e => setPrize3rd(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Custom Positions List */}
            {customPrizes.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: '800', color: '#374151' }}>Custom Positions & Special Awards</label>
                {customPrizes.map((cp, idx) => (
                  <div key={cp.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'center' }}>
                    <input
                      placeholder="Position Title (e.g. Man of the Match / Best Goalkeeper)"
                      value={cp.title}
                      onChange={e => updateCustomPrize(idx, 'title', e.target.value)}
                      style={inputStyle}
                    />
                    <input
                      placeholder="Prize / Reward (e.g. ₹5,000 + Golden Boot)"
                      value={cp.prize}
                      onChange={e => updateCustomPrize(idx, 'prize', e.target.value)}
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => removeCustomPrize(idx)}
                      style={{ ...iconBtnStyle, color: '#DC2626', background: '#FEE2E2', padding: '10px' }}
                      title="Remove Position"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Position Button */}
            <button
              type="button"
              onClick={addCustomPrize}
              style={{
                padding: '8px 16px',
                borderRadius: '10px',
                border: '1.5px dashed #084734',
                background: 'white',
                color: '#084734',
                fontWeight: '800',
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <Plus size={16} /> Add Position / Special Award
            </button>
          </div>

          {/* Status */}
          <div style={fieldGroup}>
            <label style={labelStyle}>Tournament Status</label>
            <select
              value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
              style={inputStyle}
            >
              <option value="upcoming">Upcoming</option>
              <option value="live">🔴 Live / In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#084734', fontWeight: '700' }}>
              ⚡ Status automatically calculates based on duration (Starts From vs Ends On). You can also override or cancel.
            </p>
          </div>

          {/* BANNER IMAGE DRAG & DROP / FILE SELECT UPLOAD */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Banner Image (Select or Drag & Drop File)</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                  handleBannerUpload(e.dataTransfer.files[0]);
                }
              }}
              style={{
                border: `2px dashed ${isDragging ? '#084734' : '#D1D5DB'}`,
                borderRadius: '16px',
                background: isDragging ? '#F0FDF4' : '#FAFAFA',
                padding: '1.5rem',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative'
              }}
            >
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    handleBannerUpload(e.target.files[0]);
                  }
                }}
                style={{
                  position: 'absolute',
                  top: 0, left: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer'
                }}
              />

              {uploadingBanner ? (
                <div style={{ padding: '1rem', color: '#084734', fontWeight: '700', fontSize: '0.9rem' }}>
                  ⏳ Uploading image banner to server...
                </div>
              ) : form.image_url ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <img
                    src={getTournamentImage(form.image_url)}
                    alt="Banner Preview"
                    style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '12px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.78rem', color: '#084734', fontWeight: '700' }}>✅ Image uploaded successfully</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setForm(prev => ({ ...prev, image_url: '' }));
                      }}
                      style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}
                    >
                      Remove / Replace
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#E0F2FE', color: '#0284C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UploadCloud size={24} />
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '800', color: '#1F2937' }}>
                    Drag & Drop image here, or <span style={{ color: '#084734', textDecoration: 'underline' }}>Browse File</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Supports PNG, JPG, JPEG, WebP (Uploaded directly to server's <code>uploads/tournaments</code> folder)
                  </div>
                </div>
              )}
            </div>


          </div>

          {/* Description */}
          <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Tournament Details & Rules (Optional)</label>
            <textarea
              rows={3}
              placeholder="Provide guidelines, eligibility, format details, schedule info..."
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {error && (
            <div style={{ gridColumn: '1 / -1', background: '#FEE2E2', color: '#DC2626', padding: '12px 16px', borderRadius: '10px', fontWeight: '700', fontSize: '0.88rem' }}>
              {error}
            </div>
          )}

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', marginTop: '8px' }}>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              <Check size={18} /> {saving ? 'Saving...' : editingTournament ? 'Update Tournament' : 'Submit for Approval'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingTournament(null); }} style={btnSecondary}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* TOURNAMENTS LIST */}
      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280', fontWeight: '600' }}>
          Loading tournaments...
        </div>
      ) : tournaments.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
          {tournaments.map(t => {
            const formatStr = t.tournament_type === 'Custom' ? t.tournament_type_custom : t.tournament_type;
            const sportStr = t.sport === 'Custom' ? t.sport_custom : t.sport;

            const startDate = t.start_date ? new Date(t.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
            const endDate = t.end_date ? new Date(t.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

            return (
              <div
                key={t._id}
                onClick={() => openTournamentModal(t)}
                style={{
                  background: '#fff', borderRadius: '20px', border: '1px solid #E9EDE8', padding: '1.5rem',
                  display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  opacity: t.is_active ? 1 : 0.7, cursor: 'pointer', transition: 'all 0.2s'
                }}
              >
                <div>
                  {/* Badges Row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{
                        background: t.is_approved ? '#DCEFB8' : '#FEF3C7',
                        color: t.is_approved ? '#084734' : '#D97706',
                        fontSize: '0.72rem', fontWeight: '800', padding: '4px 10px', borderRadius: '20px'
                      }}>
                        {t.is_approved ? '✅ Approved' : '⏳ Pending Approval'}
                      </span>
                      <span style={{
                        background: t.status === 'live' ? '#DCEFB8' : t.status === 'completed' ? '#F3F4F6' : t.status === 'cancelled' ? '#FEE2E2' : '#E0F2FE',
                        color: t.status === 'live' ? '#084734' : t.status === 'completed' ? '#374151' : t.status === 'cancelled' ? '#DC2626' : '#0284C7',
                        fontSize: '0.72rem', fontWeight: '800', padding: '4px 10px', borderRadius: '20px'
                      }}>
                        {t.status === 'live' ? '🔴 Live Now' : t.status === 'completed' ? '🏁 Completed' : t.status === 'cancelled' ? '❌ Cancelled' : '🟢 Upcoming'}
                      </span>
                      <span style={{
                        background: t.is_active ? '#E0F2FE' : '#F3F4F6',
                        color: t.is_active ? '#0284C7' : '#6B7280',
                        fontSize: '0.72rem', fontWeight: '800', padding: '4px 10px', borderRadius: '20px'
                      }}>
                        {t.is_active ? 'Active' : 'Deactivated'}
                      </span>
                    </div>

                    <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#084734', background: '#F0FDF4', padding: '4px 10px', borderRadius: '20px' }}>
                      {sportStr}
                    </span>
                  </div>

                  <h3 style={{ fontSize: '1.25rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '8px', lineHeight: 1.3 }}>
                    {t.name}
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.85rem', color: '#4B5563', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MapPin size={15} color="#084734" />
                      <span style={{ fontWeight: '600', color: '#1F2937' }}>{t.turf_name}</span> · {t.address}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#F8FAF7', padding: '8px 12px', borderRadius: '10px', border: '1px solid #EEF2E6' }}>
                      <div style={{ fontSize: '0.8rem', color: '#161616', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} color="#084734" />
                        <span><strong>Starts From:</strong> {startDate || 'TBD'}</span>
                      </div>
                      {endDate && (
                        <div style={{ fontSize: '0.8rem', color: '#4B5563', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '20px' }}>
                          <span><strong>Ends On:</strong> {endDate}</span>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Users size={15} color="#084734" />
                      <span>
                        Format: <strong>{formatStr}</strong> ({t.team_size}v{t.team_size})
                      </span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>🎂 <strong>Age Limit:</strong> {t.is_open_age || (!t.min_age && !t.max_age) ? 'Open for All Ages' : `${t.min_age || 0} - ${t.max_age || 'Any'} Years`}</span>
                    </div>
                  </div>

                  <div style={{ background: '#F9FAFB', padding: '10px 14px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: '700', textTransform: 'uppercase' }}>Teams Limit</div>
                      <div style={{ fontWeight: '800', fontSize: '0.95rem', color: '#111827' }}>
                        {t.is_unlimited_teams ? 'Unlimited (∞)' : `${t.max_teams} Teams`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: '700', textTransform: 'uppercase' }}>Entry Fee</div>
                      <div style={{ fontWeight: '800', fontSize: '0.95rem', color: '#084734' }}>
                        {t.entry_fee > 0 ? `₹${t.entry_fee}` : 'FREE'}
                      </div>
                    </div>
                  </div>

                  {t.prize_pool && (
                    <div style={{ fontSize: '0.82rem', color: '#D97706', fontWeight: '800', background: '#FEF3C7', padding: '6px 12px', borderRadius: '8px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Trophy size={15} /> Prize: {t.prize_pool}
                    </div>
                  )}

                </div>

                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', borderTop: '1px solid #F3F4F6', paddingTop: '1rem', flexWrap: 'wrap' }}
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    onClick={() => openTournamentModal(t)}
                    style={{
                      ...iconBtnStyle,
                      color: '#FFFFFF',
                      background: '#084734',
                      border: 'none',
                      fontWeight: '800',
                      flex: 1
                    }}
                    title="View full details & registered teams"
                  >
                    <Eye size={16} /> View Details & Teams
                  </button>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleToggleActive(t)}
                      style={{
                        ...iconBtnStyle,
                        color: t.is_active ? '#92400E' : '#065F46',
                        background: t.is_active ? '#FEF3C7' : '#D1FAE5',
                        border: t.is_active ? '1px solid #FDE68A' : '1px solid #A7F3D0'
                      }}
                      title={t.is_active ? "Deactivate tournament" : "Activate tournament"}
                    >
                      {t.is_active ? <X size={16} color="#DC2626" /> : <Check size={16} color="#059669" />}
                      {t.is_active ? 'Deactivate' : 'Activate'}
                    </button>

                    <button
                      onClick={() => openEdit(t)}
                      style={{ ...iconBtnStyle, color: '#084734', background: '#DCEFB8' }}
                      title="Edit tournament"
                    >
                      <Edit2 size={16} /> Edit
                    </button>

                    <button
                      onClick={() => handleDelete(t._id)}
                      style={{ ...iconBtnStyle, color: '#DC2626', background: '#FEE2E2' }}
                      title="Delete tournament"
                    >
                      <Trash2 size={16} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: '#fff', padding: '4rem 2rem', borderRadius: '24px', border: '1.5px dashed #D1D5DB', textAlign: 'center' }}>
          <Trophy size={48} color="#9CA3AF" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1F2937', marginBottom: '4px' }}>No Tournaments Found</h3>
          <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>You haven't created any tournaments yet.</p>
          <button onClick={openCreate} style={btnPrimary}>
            <Plus size={18} /> Host New Tournament
          </button>
        </div>
      )}

      {/* PARTNER TOURNAMENT DETAIL & REGISTERED TEAMS MODAL */}
      {selectedTournament && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)', zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '680px', width: '100%',
            maxHeight: '92vh', overflowY: 'auto', padding: '2rem', position: 'relative',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out'
          }}>
            <button
              onClick={() => { setSelectedTournament(null); setExtendingRegDate(false); setNewRegCloseDate(''); }}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#374151' }}
            >
              <X size={20} />
            </button>

            {/* Badges Header */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <span style={{
                background: selectedTournament.is_approved ? '#DCEFB8' : '#FEF3C7',
                color: selectedTournament.is_approved ? '#084734' : '#D97706',
                fontSize: '0.78rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px'
              }}>
                {selectedTournament.is_approved ? '✅ Approved by Admin' : '⏳ Pending Admin Approval'}
              </span>
              <span style={{
                background: selectedTournament.is_active ? '#E0F2FE' : '#F3F4F6',
                color: selectedTournament.is_active ? '#0284C7' : '#6B7280',
                fontSize: '0.78rem', fontWeight: '800', padding: '4px 12px', borderRadius: '20px'
              }}>
                {selectedTournament.is_active ? '🟢 Active' : '⏸️ Deactivated'}
              </span>
            </div>

            <img
              src={getTournamentImage(selectedTournament.image_url)}
              alt={selectedTournament.name}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop&q=80';
              }}
              style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '16px', marginBottom: '1.25rem' }}
            />

            <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '0.5rem', lineHeight: 1.2 }}>
              {selectedTournament.name}
            </h2>

            {/* STATUS UPDATE CONTROL FOR PARTNER */}
            <div style={{ background: '#F0FDF4', padding: '1rem 1.25rem', borderRadius: '16px', border: '1.5px solid #BBF7D0', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase' }}>Current Status</div>
                <div style={{ fontSize: '1.1rem', fontWeight: '900', color: '#0D1F0F', textTransform: 'capitalize' }}>
                  {selectedTournament.status === 'live' ? '🔴 Live Now' : selectedTournament.status === 'completed' ? '🏁 Completed' : selectedTournament.status === 'cancelled' ? '❌ Cancelled' : '🟢 Upcoming'}
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#374151' }}>Change Status:</span>
                <select
                  value={selectedTournament.status || 'upcoming'}
                  disabled={updatingStatus}
                  onChange={e => handleStatusChange(selectedTournament._id, e.target.value, selectedTournament)}
                  style={{ padding: '8px 12px', borderRadius: '10px', border: '1.5px solid #084734', background: '#DCEFB8', color: '#084734', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  <option value="upcoming">🟢 Upcoming</option>
                  <option value="live">🔴 Live Now</option>
                  <option value="completed">🏁 Completed</option>
                  <option value="cancelled">❌ Cancelled</option>
                </select>
                <button
                  type="button"
                  onClick={async () => {
                    const newActive = !selectedTournament.is_active;
                    await handleToggleActive(selectedTournament);
                    setSelectedTournament(prev => ({ ...prev, is_active: newActive }));
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '10px',
                    border: selectedTournament.is_active ? '1.5px solid #FDE68A' : '1.5px solid #A7F3D0',
                    background: selectedTournament.is_active ? '#FEF3C7' : '#D1FAE5',
                    color: selectedTournament.is_active ? '#92400E' : '#065F46',
                    fontWeight: '800',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {selectedTournament.is_active ? <X size={15} color="#DC2626" /> : <Check size={15} color="#059669" />}
                  {selectedTournament.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>

            {/* Details Box */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem', color: '#4B5563', marginBottom: '1.5rem', background: '#F9FAFB', padding: '1.25rem', borderRadius: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={18} color="#084734" />
                <span style={{ fontWeight: '700', color: '#1F2937' }}>{selectedTournament.turf_name}</span> · {selectedTournament.address} (City: {selectedTournament.city || 'N/A'})
              </div>
              {selectedTournament.start_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#374151', fontSize: '0.85rem' }}>
                  <Calendar size={16} color="#084734" />
                  <span><strong>Starts From:</strong> {new Date(selectedTournament.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {(() => {
                const regClosed = selectedTournament.registration_close_date && new Date() > new Date(selectedTournament.registration_close_date);
                return selectedTournament.registration_close_date ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: regClosed ? '#dc2626' : '#d97706', fontSize: '0.85rem', fontWeight: '600' }}>
                        <Calendar size={16} color={regClosed ? '#dc2626' : '#d97706'} />
                        <span><strong>Registration {regClosed ? 'Closed' : 'Closes'}:</strong> {new Date(selectedTournament.registration_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        {regClosed && <span style={{ background: '#FEE2E2', color: '#DC2626', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '20px', fontWeight: '800' }}>CLOSED</span>}
                      </div>
                      <button
                        onClick={() => {
                          setExtendingRegDate(!extendingRegDate);
                          setNewRegCloseDate(
                            selectedTournament.registration_close_date && typeof selectedTournament.registration_close_date === 'string'
                              ? selectedTournament.registration_close_date.slice(0, 16)
                              : selectedTournament.registration_close_date
                              ? new Date(selectedTournament.registration_close_date).toISOString().slice(0, 16)
                              : ''
                          );
                        }}
                        style={{ background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE', padding: '4px 10px', borderRadius: '8px', fontWeight: '700', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        📅 {extendingRegDate ? 'Cancel' : 'Extend Date'}
                      </button>
                    </div>
                    {extendingRegDate && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#EEF2FF', padding: '10px 12px', borderRadius: '10px', border: '1px solid #C7D2FE', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: '700', color: '#4F46E5', whiteSpace: 'nowrap' }}>New Closing Date & Time:</label>
                        <input
                          type="datetime-local"
                          value={newRegCloseDate}
                          onChange={e => setNewRegCloseDate(e.target.value)}
                          style={{ flex: 1, minWidth: '180px', padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #A5B4FC', fontSize: '0.88rem', fontWeight: '600', background: 'white', color: '#1F2937', outline: 'none' }}
                        />
                        <button
                          onClick={handleExtendRegDate}
                          disabled={savingExtend || !newRegCloseDate}
                          style={{ background: '#4F46E5', color: 'white', border: 'none', padding: '7px 16px', borderRadius: '8px', fontWeight: '800', fontSize: '0.82rem', cursor: savingExtend ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {savingExtend ? 'Saving...' : '✅ Save'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.82rem', color: '#6B7280' }}>⏰ No registration closing date set.</span>
                    <button
                      onClick={() => { setExtendingRegDate(!extendingRegDate); setNewRegCloseDate(''); }}
                      style={{ background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE', padding: '4px 10px', borderRadius: '8px', fontWeight: '700', fontSize: '0.72rem', cursor: 'pointer' }}
                    >
                      📅 {extendingRegDate ? 'Cancel' : 'Set Closing Date'}
                    </button>
                    {extendingRegDate && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#EEF2FF', padding: '10px 12px', borderRadius: '10px', border: '1px solid #C7D2FE', flexWrap: 'wrap', width: '100%' }}>
                        <input
                          type="datetime-local"
                          value={newRegCloseDate}
                          onChange={e => setNewRegCloseDate(e.target.value)}
                          style={{ flex: 1, minWidth: '180px', padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #A5B4FC', fontSize: '0.88rem', fontWeight: '600', background: 'white', color: '#1F2937', outline: 'none' }}
                        />
                        <button
                          onClick={handleExtendRegDate}
                          disabled={savingExtend || !newRegCloseDate}
                          style={{ background: '#4F46E5', color: 'white', border: 'none', padding: '7px 16px', borderRadius: '8px', fontWeight: '800', fontSize: '0.82rem', cursor: savingExtend ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                        >
                          {savingExtend ? 'Saving...' : '✅ Save'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
              {selectedTournament.end_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={18} color="#084734" />
                  <span><strong>Ends On:</strong> {new Date(selectedTournament.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={18} color="#084734" />
                <span><strong>Format:</strong> {selectedTournament.tournament_type === 'Custom' ? selectedTournament.tournament_type_custom : selectedTournament.tournament_type} ({selectedTournament.team_size}v{selectedTournament.team_size})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎂 <strong>Age Limit:</strong> {selectedTournament.is_open_age || (!selectedTournament.min_age && !selectedTournament.max_age) ? 'Open for All Ages' : `${selectedTournament.min_age || 0} - ${selectedTournament.max_age || 'Any'} Years`}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>👥 <strong>Squad Players Range:</strong> {selectedTournament.min_team_size || selectedTournament.team_size || 5} to {selectedTournament.max_team_size || selectedTournament.team_size || 5} Players per team</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Trophy size={18} color="#084734" />
                <span><strong>Entry Fee:</strong> {selectedTournament.entry_fee > 0 ? `₹${selectedTournament.entry_fee} per team` : 'FREE ENTRY'}</span>
              </div>

              {/* Prize Rewards Cards */}
              {Boolean(selectedTournament?.prize_pool || selectedTournament?.prize_1st) && (() => {
                const { prize1st, prize2nd, prize3rd } = parsePrizeBreakdown(selectedTournament);
                return (
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#084734' }}>Prize Rewards Breakdown 🏆</div>
                    
                    {prize1st && (
                      <div style={{ background: '#FEF3C7', border: '1.5px solid #FCD34D', padding: '10px 14px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Trophy size={24} color="#D97706" />
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#92400E', textTransform: 'uppercase' }}>1ST PLACE (CHAMPIONS)</div>
                          <div style={{ fontSize: '1.05rem', fontWeight: '900', color: '#B45309' }}>{prize1st}</div>
                        </div>
                      </div>
                    )}

                    {prize2nd && (
                      <div style={{ background: '#F1F5F9', border: '1.5px solid #CBD5E1', padding: '10px 14px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Trophy size={22} color="#64748B" />
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase' }}>2ND PLACE (RUNNERS UP)</div>
                          <div style={{ fontSize: '1rem', fontWeight: '900', color: '#334155' }}>{prize2nd}</div>
                        </div>
                      </div>
                    )}

                    {prize3rd && (
                      <div style={{ background: '#FFF7ED', border: '1.5px solid #FDBA74', padding: '10px 14px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Trophy size={20} color="#C2410C" />
                        <div>
                          <div style={{ fontSize: '0.7rem', fontWeight: '800', color: '#9A3412', textTransform: 'uppercase' }}>3RD PLACE</div>
                          <div style={{ fontSize: '1rem', fontWeight: '900', color: '#C2410C' }}>{prize3rd}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* REGISTERED TEAMS & PLAYERS LIST */}
            {(() => {
              const activeRegistrations = registrations.filter(reg =>
                reg.payment_status === 'completed'
              );

              return (
                <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.5rem', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#0D1F0F', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Users size={20} color="#084734" /> Registered Teams ({activeRegistrations.length}{selectedTournament.is_unlimited_teams ? '' : ` / ${selectedTournament.max_teams || '∞'}`})
                    </h4>
                    <span style={{ fontSize: '0.78rem', color: '#084734', background: '#DCEFB8', padding: '4px 10px', borderRadius: '20px', fontWeight: '800' }}>
                      {activeRegistrations.length} Teams Enrolled
                    </span>
                  </div>

                  {loadingRegistrations ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6B7280', fontSize: '0.88rem' }}>
                      Loading registered teams...
                    </div>
                  ) : activeRegistrations.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {activeRegistrations.map((reg, idx) => (
                    <div
                      key={reg._id || idx}
                      style={{
                        background: '#F9FAFB', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '12px 16px',
                        cursor: 'pointer', transition: 'all 0.2s'
                      }}
                      onClick={() => setSelectedTeam(selectedTeam === reg._id ? null : reg._id)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div>
                          <div style={{ fontWeight: '800', color: '#0D1F0F', fontSize: '0.95rem' }}>
                            {idx + 1}. {reg.team_name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#4B5563', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span>Captain: <strong>{reg.captain_name}</strong></span>
                            <span>📞 {reg.captain_phone}</span>
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <span style={{ background: '#DCEFB8', color: '#084734', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800' }}>
                            ₹{reg.amount_paid} Paid
                          </span>
                          <div style={{ fontSize: '0.72rem', color: '#9CA3AF', marginTop: '2px' }}>
                            {reg.created_at ? new Date(reg.created_at).toLocaleDateString('en-IN') : ''}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Registered User Details */}
                      {selectedTeam === reg._id && (
                        <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #D1D5DB', fontSize: '0.85rem', color: '#374151', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px', background: 'white', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                            <div><strong>Captain Email:</strong> {reg.captain_email || 'N/A'}</div>
                            <div><strong>City / Location:</strong> {reg.city || 'N/A'}</div>
                            <div><strong>Primary Contact:</strong> 📞 {reg.captain_phone}</div>
                            <div><strong>Alternate Phone:</strong> {reg.alternate_phone ? `📞 ${reg.alternate_phone}` : 'N/A'}</div>
                            <div><strong>Player Count:</strong> {reg.player_count} Players</div>
                            <div><strong>Payment Status:</strong> <span style={{ color: '#16a34a', fontWeight: '800' }}>{reg.payment_status?.toUpperCase()}</span></div>
                            <div><strong>PayU ID (mihpayid):</strong> {reg.payu_mihpayid || 'N/A'}</div>
                            <div><strong>Bank Ref / RRN:</strong> {reg.bank_ref_num || 'N/A'}</div>
                            <div><strong>Payment Mode / UPI:</strong> {reg.upi_id ? `📱 ${reg.upi_id}` : (reg.payment_mode || 'PayU')}</div>
                          </div>
                          {reg.additional_contact && (
                            <div style={{ background: '#FFFBEB', padding: '8px 12px', borderRadius: '8px', border: '1px solid #FCD34D', color: '#B45309' }}>
                              <strong>Additional Contact / Notes:</strong> {reg.additional_contact}
                            </div>
                          )}
                          {reg.player_names && (
                            <div style={{ background: 'white', padding: '10px 12px', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                              <strong>Roster / Player Names:</strong>
                              <p style={{ margin: '4px 0 0', color: '#4B5563', whiteSpace: 'pre-line' }}>{reg.player_names}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ background: '#F9FAFB', padding: '1.5rem', borderRadius: '14px', border: '1px dashed #D1D5DB', textAlign: 'center', color: '#6B7280', fontSize: '0.85rem' }}>
                  No team registrations submitted yet for this tournament.
                </div>
              )}
            </div>
          );
        })()}

            {/* ── Leaderboard Section (only for completed tournaments) ── */}
            {selectedTournament.status === 'completed' && (
              <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.5rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Trophy size={20} color="#D97706" />
                    <span style={{ fontWeight: '900', fontSize: '1rem', color: '#0D1F0F' }}>Leaderboard & Winners</span>
                    <span style={{ fontSize: '0.72rem', color: '#6B7280', background: '#F3F4F6', padding: '2px 8px', borderRadius: '20px' }}>Optional</span>
                  </div>
                  <button
                    onClick={() => setShowLeaderboardEditor(!showLeaderboardEditor)}
                    style={{ background: showLeaderboardEditor ? '#FEE2E2' : '#FEF3C7', color: showLeaderboardEditor ? '#DC2626' : '#D97706', border: 'none', padding: '7px 14px', borderRadius: '10px', fontWeight: '800', fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {showLeaderboardEditor ? '✕ Cancel' : '✏️ Edit Results'}
                  </button>
                </div>

                {/* Viewer (always shown when entries exist) */}
                <TournamentLeaderboard key={leaderboardKey} tournamentId={selectedTournament._id} token={token} />

                {/* Editor (shown when editing) */}
                {showLeaderboardEditor && (
                  <div style={{ background: '#FFFBEB', border: '1.5px solid #FDE68A', borderRadius: '16px', padding: '1.25rem', marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.8rem', color: '#92400E', fontWeight: '700', marginBottom: '1rem' }}>
                      ℹ️ All fields are optional. Fill in what you'd like to publish publicly.
                    </div>

                    {leaderboardEntries.map((entry, idx) => (
                      <div key={entry.id} style={{ background: 'white', borderRadius: '12px', border: '1px solid #FDE68A', padding: '12px 14px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontWeight: '800', color: '#92400E', fontSize: '0.85rem' }}>
                            {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`} Rank {entry.rank}
                          </span>
                          {entry.rank > 3 && (
                            <button onClick={() => removeLeaderboardRow(entry.id)} style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.75rem' }}>✕ Remove</button>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '700', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Rank Label</label>
                            <input
                              type="text"
                              placeholder={entry.rank <= 3 ? (entry.rank === 1 ? '1st Place' : entry.rank === 2 ? '2nd Place' : '3rd Place') : 'e.g. Best Goalkeeper'}
                              value={entry.rank_label}
                              onChange={e => updateLeaderboardRow(entry.id, 'rank_label', e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '0.85rem', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '700', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Team / Player Name</label>
                            <input
                              type="text"
                              placeholder="Team or player name"
                              value={entry.team_name}
                              onChange={e => updateLeaderboardRow(entry.id, 'team_name', e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '0.85rem', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '700', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Captain Name (optional)</label>
                            <input
                              type="text"
                              placeholder="Captain / player"
                              value={entry.captain_name}
                              onChange={e => updateLeaderboardRow(entry.id, 'captain_name', e.target.value.replace(/[^a-zA-Z\s.-]/g, ''))}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '0.85rem', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '0.72rem', fontWeight: '700', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Prize (optional)</label>
                            <input
                              type="text"
                              placeholder="e.g. ₹10,000 cash"
                              value={entry.prize}
                              onChange={e => updateLeaderboardRow(entry.id, 'prize', e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '0.85rem', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                            />
                          </div>
                          <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ fontSize: '0.72rem', fontWeight: '700', color: '#6B7280', display: 'block', marginBottom: '3px' }}>Notes (optional)</label>
                            <input
                              type="text"
                              placeholder="Any additional notes"
                              value={entry.notes}
                              onChange={e => updateLeaderboardRow(entry.id, 'notes', e.target.value)}
                              style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '0.85rem', fontWeight: '600', boxSizing: 'border-box', outline: 'none' }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                      <button
                        onClick={addLeaderboardRow}
                        style={{ background: 'white', color: '#D97706', border: '1.5px dashed #FDE68A', padding: '8px 14px', borderRadius: '10px', fontWeight: '800', fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <Plus size={14} /> Add More Entry
                      </button>
                      <button
                        onClick={handleSaveLeaderboard}
                        disabled={savingLeaderboard}
                        style={{ background: '#084734', color: '#CEF17B', border: 'none', padding: '10px 22px', borderRadius: '12px', fontWeight: '800', fontSize: '0.88rem', cursor: savingLeaderboard ? 'wait' : 'pointer' }}
                      >
                        {savingLeaderboard ? 'Saving...' : '🏆 Save Leaderboard'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Bar */}
            <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.25rem', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  const tToEdit = selectedTournament;
                  setSelectedTournament(null);
                  setExtendingRegDate(false);
                  setNewRegCloseDate('');
                  openEdit(tToEdit);
                }}
                style={{ background: '#084734', color: '#CEF17B', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Edit2 size={16} /> Edit Tournament Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const btnPrimary = { background: '#084734', color: '#CEF17B', border: 'none', padding: '12px 24px', borderRadius: '12px', fontWeight: '800', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 6px 18px rgba(8,71,52,0.2)' };
const btnSecondary = { background: '#fff', color: '#374151', border: '1.5px solid #E9EDE8', padding: '12px 24px', borderRadius: '12px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' };
const closeBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex', padding: '4px' };
const iconBtnStyle = { background: '#F9FAFB', border: '1px solid #E5E7EB', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#374151' };
const formCardStyle = { background: '#fff', padding: '1.8rem', borderRadius: '24px', border: '1.5px solid #E9EDE8', marginBottom: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' };
const fieldGroup = { display: 'flex', flexDirection: 'column', gap: '6px' };
const labelStyle = { fontSize: '0.78rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' };
const inputStyle = { padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #E5E7EB', fontSize: '0.9rem', fontWeight: '600', outline: 'none', width: '100%', boxSizing: 'border-box', background: '#F9FAFB', color: '#111827' };
